<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useRouter } from 'vue-router';
import { useMapGetter } from 'dashboard/composables/store';
import analyzersApi from 'dashboard/api/analysis/analyzers';
import runsApi from 'dashboard/api/analysis/runs';
import resultsApi from 'dashboard/api/analysis/results';
import ReportHeader from './components/ReportHeader.vue';
import ReportFilters from './components/ReportFilters.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import RunPreviewDialog from './components/quality/RunPreviewDialog.vue';
import RunStatusCard from './components/quality/RunStatusCard.vue';
import QualitySummary from './components/quality/QualitySummary.vue';
import ResultsTable from './components/quality/ResultsTable.vue';

const POLL_INTERVAL = 4000;

const { t } = useI18n();
const router = useRouter();
const accountId = useMapGetter('getCurrentAccountId');

const analyzers = ref([]);
const selectedAnalyzerId = ref(null);
const range = ref({ from: 0, to: 0 });
const summary = ref(null);
const results = ref([]);
const runs = ref([]);
const isLoading = ref(false);
const previewDialogRef = ref(null);
let pollTimer = null;

const analyzerOptions = computed(() =>
  analyzers.value.map(analyzer => ({
    value: analyzer.id,
    label: analyzer.name,
  }))
);

const selectedAnalyzer = computed(() =>
  analyzers.value.find(analyzer => analyzer.id === selectedAnalyzerId.value)
);

const activeRun = computed(() =>
  runs.value.find(run => ['pending', 'processing'].includes(run.status))
);

const latestRun = computed(() => activeRun.value ?? runs.value[0]);

const hasResults = computed(() => Boolean(summary.value?.total));

const rubric = computed(() =>
  (selectedAnalyzer.value?.criteria ?? []).filter(criterion => criterion.active)
);

const requestParams = computed(() => ({
  analyzer_id: selectedAnalyzerId.value,
  from: range.value.from,
  to: range.value.to,
}));

const fetchAnalyzers = async () => {
  const { data } = await analyzersApi.get();
  analyzers.value = data.payload.filter(analyzer => analyzer.active);
  if (!selectedAnalyzerId.value && analyzers.value.length) {
    selectedAnalyzerId.value = analyzers.value[0].id;
  }
};

const fetchReport = async () => {
  if (!selectedAnalyzerId.value || !range.value.to) return;
  isLoading.value = true;
  try {
    const [summaryResponse, resultsResponse, runsResponse] = await Promise.all([
      resultsApi.summary(requestParams.value),
      resultsApi.get(requestParams.value),
      runsApi.get({ analyzerId: selectedAnalyzerId.value }),
    ]);
    summary.value = summaryResponse.data;
    results.value = resultsResponse.data.payload;
    runs.value = runsResponse.data.payload;
  } finally {
    isLoading.value = false;
  }
};

// Only while something is actually running: a finished report has nothing to poll for.
const syncActiveRun = async () => {
  if (!activeRun.value) return;
  const { data } = await runsApi.show(activeRun.value.id);
  const index = runs.value.findIndex(run => run.id === data.id);
  if (index !== -1) runs.value.splice(index, 1, data);
  if (!['pending', 'processing'].includes(data.status)) await fetchReport();
};

const stopPolling = () => {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = null;
};

const startPolling = () => {
  stopPolling();
  pollTimer = setInterval(syncActiveRun, POLL_INTERVAL);
};

watch(activeRun, run => (run ? startPolling() : stopPolling()));

const onFilterChange = ({ from, to }) => {
  range.value = { from, to };
  fetchReport();
};

watch(selectedAnalyzerId, fetchReport);

const openPreview = async () => {
  previewDialogRef.value.open();
  previewDialogRef.value.setLoading(true);
  try {
    const { data } = await runsApi.preview(requestParams.value);
    previewDialogRef.value.setPreview(data);
  } finally {
    previewDialogRef.value.setLoading(false);
  }
};

const onReevaluateChange = async reevaluate => {
  previewDialogRef.value.setLoading(true);
  try {
    const { data } = await runsApi.preview({
      ...requestParams.value,
      reevaluate,
    });
    previewDialogRef.value.setPreview(data);
  } finally {
    previewDialogRef.value.setLoading(false);
  }
};

const startRun = async ({ reevaluate }) => {
  try {
    const { data } = await runsApi.create({
      analyzer_id: selectedAnalyzerId.value,
      from: range.value.from,
      to: range.value.to,
      reevaluate,
    });
    runs.value = [data, ...runs.value];
    previewDialogRef.value.close();
    startPolling();
  } catch (error) {
    useAlert(
      error?.response?.data?.message ?? t('REPORT.DATA_FETCHING_FAILED')
    );
  }
};

const cancelRun = async run => {
  const { data } = await runsApi.cancel(run.id);
  const index = runs.value.findIndex(item => item.id === data.id);
  if (index !== -1) runs.value.splice(index, 1, data);
  stopPolling();
  await fetchReport();
};

const goToSetup = () =>
  router.push({
    name: 'analysis_analyzers_index',
    params: { accountId: accountId.value },
  });

onMounted(fetchAnalyzers);
onBeforeUnmount(stopPolling);
</script>

<template>
  <ReportHeader :header-title="t('ANALYSIS.QUALITY.HEADER')">
    <NextButton
      v-if="analyzers.length"
      icon="i-lucide-sparkles"
      size="sm"
      :label="t('ANALYSIS.QUALITY.RUN')"
      :disabled="Boolean(activeRun)"
      @click="openPreview"
    />
  </ReportHeader>

  <div class="flex flex-col gap-4 pb-6">
    <template v-if="analyzers.length">
      <ReportFilters
        :show-entity-filter="false"
        :show-group-by="false"
        :show-business-hours="false"
        @filter-change="onFilterChange"
      />

      <div class="flex items-center gap-3">
        <span class="text-sm text-n-slate-11">
          {{ t('ANALYSIS.QUALITY.ANALYZER') }}
        </span>
        <Select
          v-model="selectedAnalyzerId"
          :options="analyzerOptions"
          class="[&>select]:min-w-56"
        />
      </div>

      <div
        v-if="rubric.length"
        class="flex flex-col gap-2 p-4 border rounded-xl border-n-weak bg-n-solid-1"
      >
        <span class="text-sm font-medium text-n-slate-12">
          {{ t('ANALYSIS.QUALITY.RUBRIC') }}
        </span>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="criterion in rubric"
            :key="criterion.criterion_id"
            class="inline-flex items-center gap-2 px-2.5 py-1 text-sm rounded-md bg-n-alpha-1 text-n-slate-12"
          >
            {{ criterion.name }}
            <span class="text-xs text-n-slate-11">
              {{ t(`ANALYSIS.CRITERIA.KIND.${criterion.kind.toUpperCase()}`) }}
            </span>
          </span>
        </div>
      </div>

      <RunStatusCard v-if="latestRun" :run="latestRun" @cancel="cancelRun" />

      <div v-if="isLoading" class="flex justify-center py-12">
        <Spinner />
      </div>

      <template v-else-if="hasResults">
        <QualitySummary :summary="summary" />
        <ResultsTable
          :results="results"
          :criteria="selectedAnalyzer?.criteria ?? []"
        />
      </template>

      <div v-else class="flex flex-col items-center gap-2 py-16 text-center">
        <p class="text-n-slate-12">{{ t('ANALYSIS.QUALITY.EMPTY') }}</p>
        <p class="max-w-md text-sm text-n-slate-11">
          {{ t('ANALYSIS.QUALITY.EMPTY_HINT') }}
        </p>
      </div>
    </template>

    <div v-else class="flex flex-col items-center gap-3 py-20">
      <p class="text-n-slate-11">{{ t('ANALYSIS.QUALITY.NO_ANALYZERS') }}</p>
      <NextButton
        :label="t('ANALYSIS.QUALITY.NO_ANALYZERS_CTA')"
        sm
        @click="goToSetup"
      />
    </div>

    <RunPreviewDialog
      ref="previewDialogRef"
      @confirm="startRun"
      @reevaluate-change="onReevaluateChange"
    />
  </div>
</template>
